import { ReactElement, ReactNode, useMemo, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnOrderState, VisibilityState } from '@tanstack/react-table';
import styled from 'styled-components';

import { Button } from '@components/button';
import { usePreviousValue } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { FocusBox } from '@components/focus-box';
import { SmallDragHandle } from '@components/form-builder/drag-handle';
import { IconProps } from '@components/icons';
import { IconColumns } from '@components/icons/columns';
import { IconDrag } from '@components/icons/drag';
import { IconInfoSmall } from '@components/icons/info-small';
import { SearchBox } from '@components/search-box';
import { Sidebar, showSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { SmallColumnIcon } from './column-icon';
import { ensureSmallIcon } from './table-utils';
import { AvailableColumn, DataItemType } from './types';

export const showFieldsSidebar = <T extends DataItemType>(props: FieldsSidebarProps<T>) => {
  showSidebar({
    component: FieldsSidebar<T>,
    options: { hasBackdrop: true, variant: 'REGULAR' },
    props,
  });
};

type FieldsSidebarProps<T extends DataItemType> = {
  availableColumns: Array<AvailableColumn<T> & { extraContent?: ReactNode }>;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  onVisibilityUpdate(newState: VisibilityState): void;
  onOrderUpdate(newOrder: ColumnOrderState): void;
};

export const FieldsSidebar = <T extends DataItemType>({
  availableColumns,
  columnVisibility: currentColumnVisibility,
  columnOrder: currentColumnOrder,
  onOrderUpdate,
  onVisibilityUpdate,
}: FieldsSidebarProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const [columnVisibility, setColumnVisibility] = useState(currentColumnVisibility);
  const [columnOrder, setColumnOrder] = useState(currentColumnOrder);

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('title')} icon={IconColumns} iconColor="elementBlue100" />
      <Sidebar.Content>
        <ColumnManager
          availableColumns={availableColumns}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          onOrderUpdate={(order) => {
            setColumnOrder(order);
            onOrderUpdate(order);
          }}
          onVisibilityUpdate={(visibility) => {
            setColumnVisibility(visibility);
            onVisibilityUpdate(visibility);
          }}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

export type ColumnManagerProps<T extends DataItemType> = FieldsSidebarProps<T>;

export const ColumnManager = <T extends DataItemType>({
  availableColumns,
  columnVisibility,
  columnOrder,
  onOrderUpdate,
  onVisibilityUpdate,
}: ColumnManagerProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<UniqueIdentifier>();
  const sensors = useSensors(useSensor(PointerSensor));

  const columns = useMemo(() => {
    const newColumns = availableColumns.slice().sort((a, b) => {
      return columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id);
    });
    return newColumns;
  }, [availableColumns, columnOrder]);

  const oldColumns = usePreviousValue(columns.map((column) => column.id));

  const newIds = useMemo(() => {
    if (!oldColumns) {
      // First render, previous value hasnt been set
      return [];
    }
    return columns
      .filter((column) => {
        return !oldColumns.includes(column.id);
      })
      .map((column) => column.id);
  }, [columns, oldColumns]);

  const onDragEnd = (event: DragEndEvent) => {
    setActiveId(undefined);
    const { active, over } = event;
    if (!over) {
      return;
    }

    const activeIndex = columnOrder.indexOf(String(active.id));
    const overIndex = columnOrder.indexOf(String(over.id));
    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    const newOrder = arrayMove(columnOrder, activeIndex, overIndex);
    onOrderUpdate(newOrder);
  };

  const onToggleAll = (isVisible: boolean = true) => {
    const newVisibility = availableColumns
      .map((column) => column.id)
      .reduce((visibility, columnId) => {
        visibility[columnId] = isVisible;
        return visibility;
      }, {});
    if (!isVisible && columnOrder.length > 0) {
      newVisibility[columnOrder[0]] = true;
    }
    onVisibilityUpdate(newVisibility);
  };

  const toggleVisibility = (columnId: string) => {
    const prev = columnVisibility[columnId];
    const hiddenCount = Object.keys(columnVisibility).filter(
      (key) => columnVisibility[key] === false
    ).length;

    if (hiddenCount >= columns.length - 1 && prev !== false) {
      return;
    }
    const newVisibility = {
      ...columnVisibility,
      [columnId]: prev === undefined ? false : !prev,
    };
    onVisibilityUpdate(newVisibility);
  };

  const searchStr = search.toLowerCase();
  const filteredColumns = columns.filter((column) => {
    return column.title.toLowerCase().includes(searchStr);
  });

  const activeColumn = activeId ? filteredColumns.find((column) => column.id === activeId) : null;

  return (
    <Stack space={6}>
      <Stack orientation="horizontal" space={2}>
        <SearchBox placeholder={t('searchPlaceholder')} onChange={setSearch} />
        <Button text={t('showAll')} variant="secondary" onClick={() => onToggleAll(true)} />
        <Button text={t('hideAll')} variant="secondary" onClick={() => onToggleAll(false)} />
      </Stack>

      {searchStr && filteredColumns.length === 0 && (
        <EmptySearchState variant="small" orientation="horizontal" skipMargin />
      )}

      <DndContext
        sensors={sensors}
        onDragEnd={onDragEnd}
        onDragStart={(event) => setActiveId(event.active.id)}
        onDragCancel={() => setActiveId(undefined)}
      >
        <DragOverlay>
          {activeColumn && (
            <OverlayContainer>
              <Switch
                size="sm"
                iconLeft={
                  activeColumn.renderHeaderIcon ? (
                    activeColumn.renderHeaderIcon()
                  ) : activeColumn.type ? (
                    <SmallColumnIcon type={activeColumn.type} />
                  ) : undefined
                }
                key={activeColumn.id}
                value={columnVisibility[activeColumn.id] !== false}
                label={activeColumn.title}
              />
              <SmallDragHandle icon={<IconDrag size={16} />} />
            </OverlayContainer>
          )}
        </DragOverlay>
        <SortableContext items={columns.map((column) => column.id)}>
          <div>
            {filteredColumns.map((column, index) => {
              const icon = column.renderHeaderIcon ? (
                ensureSmallIcon(column.renderHeaderIcon())
              ) : column.type ? (
                <SmallColumnIcon type={column.type} />
              ) : undefined;
              return (
                <FocusBox isFocused={newIds.includes(column.id)} key={column.id}>
                  <ColumnItem
                    icon={icon}
                    index={index}
                    column={column}
                    onToggle={toggleVisibility}
                    isActive={columnVisibility[column.id] !== false}
                    isFiltered={search.length > 0}
                  />
                </FocusBox>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </Stack>
  );
};

type ColumnItemProps<T extends DataItemType> = {
  onToggle(columnId: string): void;
  column: AvailableColumn<T> & { extraContent?: ReactNode };
  index: number;
  isActive: boolean;
  isFiltered: boolean;
  icon?: ReactElement<IconProps>;
};

const ColumnItem = <T extends DataItemType>({
  column,
  onToggle,
  index,
  isActive,
  isFiltered,
  icon,
}: ColumnItemProps<T>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    data: {
      order: index,
    },
  });

  return (
    <ItemContainer
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
    >
      <Switch
        size="sm"
        iconLeft={icon}
        key={column.id}
        value={isActive}
        label={column.title}
        onChange={() => onToggle(column.id)}
      />
      <Stack space={1} orientation="horizontal" align="center">
        {column.tooltip && (
          <Tooltip placement="left" content={column.tooltip()}>
            <span style={{ display: 'flex' }}>
              <IconInfoSmall color="purple50" />
            </span>
          </Tooltip>
        )}
        {column.extraContent}
        {!isFiltered && (
          <SmallDragHandle {...attributes} {...listeners} icon={<IconDrag size={16} />} />
        )}
      </Stack>
    </ItemContainer>
  );
};

const locale = {
  en: {
    title: 'Show / Hide fields',
    showAll: 'Show all',
    hideAll: 'Hide all',
    searchPlaceholder: 'Search fields...',
  },
  is: {
    title: 'Sýna/Fela Dálka',
    showAll: 'Sýna alla',
    hideAll: 'Fela alla',
    searchPlaceholder: 'Leita að dálk...',
  },
};

const ItemContainer = styled(Stack).attrs({
  justify: 'space-between',
  orientation: 'horizontal',
})({
  padding: `${pxToRem(4)} 0`,
});

const OverlayContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  align: 'center',
})({
  boxShadow: theme.shadow.medium,
  borderRadius: pxToRem(4),
  margin: `0 ${pxToRem(-4)}`,
  backgroundColor: theme.color.backgroundLightDark,
  padding: pxToRem(4),
});
